import { GoogleDriveResourceKind } from '@src/service/services/google-drive'

export interface ParsedGoogleDriveUrl {
  id: string
  kind: GoogleDriveResourceKind
  resourceKey?: string
}

const ID_PATTERN = /^[a-zA-Z0-9_-]{10,}$/

function cleanId(value: string | null): string | undefined {
  if (!value) return undefined
  const trimmed = value.trim()
  return ID_PATTERN.test(trimmed) ? trimmed : undefined
}

function resourceKeyFromSearch(search: URLSearchParams): string | undefined {
  return search.get('resourcekey') || search.get('resourceKey') || undefined
}

export function parseGoogleDriveUrl(rawUrl: string): ParsedGoogleDriveUrl | undefined {
  let url: URL
  try {
    url = new URL(rawUrl.trim())
  } catch {
    return undefined
  }

  const hostname = url.hostname.toLowerCase()
  const path = url.pathname
  const resourceKey = resourceKeyFromSearch(url.searchParams)

  if (hostname === 'drive.google.com') {
    const folderMatch = path.match(/\/drive\/(?:u\/\d+\/)?folders\/([^/?#]+)/)
    const fileMatch = path.match(/\/file\/d\/([^/?#]+)/)
    const openId = cleanId(url.searchParams.get('id'))

    if (folderMatch) {
      const id = cleanId(folderMatch[1])
      return id ? { id, kind: 'folder', resourceKey } : undefined
    }
    if (fileMatch) {
      const id = cleanId(fileMatch[1])
      return id ? { id, kind: 'file', resourceKey } : undefined
    }
    if (openId) {
      return { id: openId, kind: 'file', resourceKey }
    }
  }

  if (hostname === 'docs.google.com') {
    const docsMatch = path.match(/\/(?:document|spreadsheets|presentation)(?:\/u\/\d+)?\/d\/([^/?#]+)/)
    if (docsMatch) {
      const id = cleanId(docsMatch[1])
      return id ? { id, kind: 'file', resourceKey } : undefined
    }
  }

  return undefined
}

export function parseGoogleDriveUrls(input: string): Array<{ url: string; parsed?: ParsedGoogleDriveUrl }> {
  return input
    .split(/\r?\n/)
    .map(url => url.trim())
    .filter(Boolean)
    .map(url => ({ url, parsed: parseGoogleDriveUrl(url) }))
}
